/**
 * Klien back office: katalog dan pengimport CSV.
 *
 * Ia menggunakan sesi pemilik yang disimpan semasa persediaan peranti. Jika sesi luput (12 jam),
 * pemilik diminta menyediakan semula — itulah kekangan ADR-0009, dan ia diterima untuk fasa ini.
 */

export interface ProductVariant {
  id: string;
  name: string | null;
  sku: string | null;
  barcode: string | null;
  unit: string;
  price_sen: number;
  cost_sen: number;
  is_active: boolean;
}

export interface Product {
  id: string;
  name: string;
  brand: string | null;
  is_active: boolean;
  variants: ProductVariant[];
}

export interface ImportSummary {
  products: number;
  variants: number;
  withStock: number;
  errors: number;
  warnings: number;
}

export interface ImportPreview {
  mode: 'preview';
  summary: ImportSummary;
  warnings: string[];
}

export interface ImportError {
  row: number;
  field: string;
  message: string;
}

export function isUnauthorized(error: unknown): boolean {
  return error instanceof AdminError && error.status === 401;
}

export class AdminError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

async function parseError(response: Response): Promise<AdminError> {
  try {
    const body = (await response.json()) as {
      error?: { code?: string; message?: string; details?: unknown };
    };
    return new AdminError(
      response.status,
      body.error?.code ?? `HTTP_${response.status}`,
      body.error?.message ?? 'permintaan gagal',
      body.error?.details,
    );
  } catch {
    return new AdminError(response.status, `HTTP_${response.status}`, 'permintaan gagal');
  }
}

export async function listProducts(
  apiBase: string,
  token: string,
  query = '',
  fetchImpl: typeof fetch = fetch,
): Promise<Product[]> {
  const response = await fetchImpl(
    `${apiBase}/v1/catalog/products?limit=200${query === '' ? '' : `&q=${encodeURIComponent(query)}`}`,
    { headers: { authorization: `Bearer ${token}` } },
  );
  if (!response.ok) throw await parseError(response);
  const body = (await response.json()) as { data: Product[] };
  return body.data;
}

export interface CreateProductInput {
  name: string;
  variants: Array<{
    name?: string;
    sku?: string;
    barcode?: string;
    unit: string;
    price_sen: number;
    cost_sen?: number;
  }>;
}

export async function createProduct(
  apiBase: string,
  token: string,
  input: CreateProductInput,
  fetchImpl: typeof fetch = fetch,
): Promise<Product> {
  const response = await fetchImpl(`${apiBase}/v1/catalog/products`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
    body: JSON.stringify(input),
  });
  if (!response.ok) throw await parseError(response);
  return (await response.json()) as Product;
}

export interface ImportCommit {
  mode: 'commit';
  summary: ImportSummary;
  productsCreated: number;
  variantsCreated: number;
  stockApplied: number;
}

export async function importCatalogCsv(
  apiBase: string,
  token: string,
  csv: string,
  mode: 'preview' | 'commit',
  fetchImpl: typeof fetch = fetch,
): Promise<ImportPreview | ImportCommit> {
  const response = await fetchImpl(`${apiBase}/v1/catalog/import`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
    body: JSON.stringify({ csv, mode }),
  });
  if (!response.ok) throw await parseError(response);
  return (await response.json()) as ImportPreview | ImportCommit;
}

// ---------------------------------------------------------------- laporan

export interface DailyReport {
  date: string;
  sales_count: number;
  gross_sen: number;
  discount_sen: number;
  tax_sen: number;
  net_sen: number;
  cogs_sen: number;
  gross_profit_sen: number;
  margin_bp: number;
  avg_basket_sen: number;
  payment_mix: Record<string, number>;
  hourly: Array<{ hour: number; total_sen: number }>;
}

export interface ProfitReport {
  from: string;
  to: string;
  revenue_sen: number;
  cogs_sen: number;
  gross_profit_sen: number;
  margin_bp: number;
  sales_count: number;
  cost_unknown_lines: number;
  days: Array<{ day: string; net_sen: number; count: number }>;
}

export interface TopItemsReport {
  from: string;
  to: string;
  items: Array<{
    variant_id: string | null;
    name: string;
    qty: number;
    unit: string;
    revenue_sen: number;
    profit_sen: number;
  }>;
}

export interface StockValuation {
  cost_sen: number;
  retail_sen: number;
  variant_count: number;
  negative_count: number;
}

async function getJson<T>(path: string, token: string, fetchImpl: typeof fetch): Promise<T> {
  const response = await fetchImpl(path, { headers: { authorization: `Bearer ${token}` } });
  if (!response.ok) throw await parseError(response);
  return (await response.json()) as T;
}

export function getDaily(
  apiBase: string,
  token: string,
  date: string,
  fetchImpl: typeof fetch = fetch,
): Promise<DailyReport> {
  return getJson(`${apiBase}/v1/reports/daily?date=${date}`, token, fetchImpl);
}

export function getProfit(
  apiBase: string,
  token: string,
  from: string,
  to: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ProfitReport> {
  return getJson(`${apiBase}/v1/reports/profit?from=${from}&to=${to}`, token, fetchImpl);
}

export function getTopItems(
  apiBase: string,
  token: string,
  from: string,
  to: string,
  fetchImpl: typeof fetch = fetch,
): Promise<TopItemsReport> {
  return getJson(`${apiBase}/v1/reports/top-items?from=${from}&to=${to}`, token, fetchImpl);
}



/**
 * Hidupkan atau matikan cukai jualan (SST).
 *
 * Lalai mati. Perubahan ini hanya menjejaskan jualan BAHARU: resit yang sudah dicetak tidak boleh
 * berubah, dan cukai yang sudah dikutip mesti kekal dalam rekod.
 */
export async function setTaxEnabled(
  apiBase: string,
  token: string,
  enabled: boolean,
  fetchImpl: typeof fetch = fetch,
): Promise<{ tax_enabled: boolean }> {
  const response = await fetchImpl(`${apiBase}/v1/auth/tenant/settings`, {
    method: 'PATCH',
    headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
    body: JSON.stringify({ tax_enabled: enabled }),
  });
  if (!response.ok) throw await parseError(response);

  const body = (await response.json()) as { settings?: { tax_enabled?: boolean } };
  return { tax_enabled: body.settings?.tax_enabled === true };
}


/**
 * Simpan kepala dan footer resit.
 *
 * Ia dihantar bersama setiap kerja cetakan, jadi perubahan berkuat kuasa pada resit seterusnya —
 * tiada fail setempat untuk disunting pada setiap PC.
 */
export async function setReceiptBranding(
  apiBase: string,
  token: string,
  header: readonly string[],
  footer: string,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  const response = await fetchImpl(`${apiBase}/v1/auth/tenant/settings`, {
    method: 'PATCH',
    headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
    body: JSON.stringify({ receipt_header: header, receipt_footer: footer }),
  });
  if (!response.ok) throw await parseError(response);
}


/**
 * Kemas kini varian: nama, harga, kos, barcode.
 *
 * Kos ialah kos SEMASA varian (bukan purata). Setiap jualan merekod kos pada masa jualan sampai ke
 * pelayan, jadi mengubah kos di sini tidak mengubah untung jualan yang sudah direkod.
 */
export async function updateVariant(
  apiBase: string,
  token: string,
  variantId: string,
  patch: {
    name?: string | null;
    price_sen?: number;
    cost_sen?: number;
    barcode?: string | null;
  },
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  const response = await fetchImpl(`${apiBase}/v1/catalog/variants/${variantId}`, {
    method: 'PATCH',
    headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!response.ok) throw await parseError(response);
}


/**
 * Isi kos banyak varian dalam satu permintaan.
 *
 * Kos tidak dihantar ke POS (ADR-0018), jadi hanya back office boleh mengisinya. Satu panggilan
 * mengelakkan 18 permintaan berasingan dan memberi satu hasil yang jelas.
 */
export async function setVariantCosts(
  apiBase: string,
  token: string,
  items: ReadonlyArray<{ variantId: string; costSen: number }>,
  fetchImpl: typeof fetch = fetch,
): Promise<{ updated: string[]; missing: string[] }> {
  const response = await fetchImpl(`${apiBase}/v1/catalog/costs`, {
    method: 'POST',
    headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
    body: JSON.stringify({
      items: items.map((item) => ({ variant_id: item.variantId, cost_sen: item.costSen })),
    }),
  });
  if (!response.ok) throw await parseError(response);
  return (await response.json()) as { updated: string[]; missing: string[] };
}


export interface TrialReport {
  from: string;
  to: string;
  sales: { count: number; total_sen: number; days_with_sales: number; needs_review: number };
  cash: { closed_shifts: number; within_one_ringgit: number; pass_rate_bp: number | null };
  sync: { samples: number; p95_seconds: number | null };
  shifts: { closed: number; p95_close_minutes: number | null };
  cost: {
    lines: number;
    lines_without_cost: number;
    variants: number;
    variants_without_cost: number;
    coverage_bp: number | null;
  };
}

/** Angka §8 untuk tetingkap tarikh. */
export function getTrial(
  apiBase: string,
  token: string,
  from: string,
  to: string,
  fetchImpl: typeof fetch = fetch,
): Promise<TrialReport> {
  return getJson(`${apiBase}/v1/reports/trial?from=${from}&to=${to}`, token, fetchImpl);
}

/** Tetapkan tarikh mula percubaan 30 hari. */
export async function setTrialStart(
  apiBase: string,
  token: string,
  date: string,
  fetchImpl: typeof fetch = fetch,
): Promise<string> {
  const response = await fetchImpl(`${apiBase}/v1/auth/tenant/settings`, {
    method: 'PATCH',
    headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
    body: JSON.stringify({ trial_started_on: date }),
  });
  if (!response.ok) throw await parseError(response);
  const body = (await response.json()) as { settings?: { trial_started_on?: string } };
  return body.settings?.trial_started_on ?? date;
}


/**
 * Muat turun katalog sebagai CSV.
 *
 * Format sama dengan pengimport, jadi fail ini boleh dibuka dalam Excel dan dimuat naik semula.
 */
export async function exportCatalogCsv(
  apiBase: string,
  token: string,
  fetchImpl: typeof fetch = fetch,
): Promise<string> {
  const response = await fetchImpl(`${apiBase}/v1/catalog/export.csv`, {
    headers: { authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw await parseError(response);
  return response.text();
}


export interface SaleRow {
  id: string;
  receipt_no: string;
  created_at: string;
  total_sen: number;
  status: string;
  cashier_name: string | null;
  device_no: number | null;
  outlet_name: string | null;
}

export interface SaleDetail {
  sale: {
    id: string;
    receipt_no: string;
    created_at: string;
    client_created_at: string;
    subtotal_sen: number;
    discount_sen: number;
    tax_sen: number;
    total_sen: number;
    paid_sen: number;
    change_sen: number;
    status: string;
    cashier_name: string | null;
    device_no: number | null;
  };
  lines: Array<{
    id: string;
    line_no: number;
    description_snapshot: string;
    qty: string;
    unit: string;
    unit_price_sen: number;
    discount_sen: number;
    tax_sen: number;
    line_total_sen: number;
  }>;
  payments: Array<{ id: string; method: string; amount_sen: number }>;
}

/** Jualan daripada semua kaunter dalam julat tarikh. */
export function listSales(
  apiBase: string,
  token: string,
  from: string,
  to: string,
  fetchImpl: typeof fetch = fetch,
): Promise<SaleRow[]> {
  return getJson<{ data: SaleRow[] }>(
    `${apiBase}/v1/sales?from=${from}&to=${to}&limit=100`,
    token,
    fetchImpl,
  ).then((body) => body.data);
}

/** Butiran satu jualan, untuk pratonton dan cetak semula. */
export function getSale(
  apiBase: string,
  token: string,
  saleId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<SaleDetail> {
  return getJson<SaleDetail>(`${apiBase}/v1/sales/${saleId}`, token, fetchImpl);
}

export interface ShiftReportRow {
  id: string;
  device_no: number;
  outlet_code: string;
  cashier_name: string | null;
  opened_at: string;
  closed_at: string | null;
  opening_cash_sen: number;
  closing_cash_sen: number | null;
  expected_cash_sen: number | null;
  variance_sen: number | null;
  sales_count: number;
  sales_total_sen: number;
  status: string;
}

export interface ShiftsReport {
  data: ShiftReportRow[];
  from: string;
  to: string;
}

export function getShifts(
  apiBase: string,
  token: string,
  from: string,
  to: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ShiftsReport> {
  return getJson(`${apiBase}/v1/reports/shifts?from=${from}&to=${to}`, token, fetchImpl);
}

export function getStockValuation(
  apiBase: string,
  token: string,
  fetchImpl: typeof fetch = fetch,
): Promise<StockValuation> {
  return getJson(`${apiBase}/v1/reports/stock-valuation`, token, fetchImpl);
}
// ---------------------------------------------------------------- peranti

export interface DeviceRow {
  id: string;
  name: string;
  device_no: number;
  outlet_code: string;
  status: string;
  paired: boolean;
  /** 'webcrypto' = kunci tidak boleh dieksport; 'exportable' = boleh diekstrak dengan DevTools. */
  key_protection?: 'webcrypto' | 'exportable';
  approved: boolean;
  approved_at: string | null;
  pairing_ip: string | null;
  pairing_user_agent: string | null;
  last_seen_at: string | null;
  created_at: string;
}

export async function listDevices(
  apiBase: string,
  token: string,
  fetchImpl: typeof fetch = fetch,
): Promise<DeviceRow[]> {
  const response = await fetchImpl(`${apiBase}/v1/devices`, {
    headers: { authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw await parseError(response);
  return ((await response.json()) as { data: DeviceRow[] }).data;
}

export interface PairingCodeResult {
  code: string;
  /** Nombor peranti yang diagihkan oleh PELAYAN, bukan oleh pelayar. */
  device_no: number;
  expires_at: string;
}

export async function createPairingCodeFor(
  apiBase: string,
  token: string,
  input: {
    outletId: string;
    name: string;
    /**
     * Nombor peranti.
     *
     * Biarkan kosong untuk peranti BAHARU (pelayan mengagihkan nombor seterusnya). Isi hanya apabila
     * menggantikan peranti yang dicabut — PC yang sama harus mengekalkan nombornya supaya resit dan
     * laporan tidak bercampur dengan kaunter lain.
     */
    deviceNo?: number;
  },
  fetchImpl: typeof fetch = fetch,
): Promise<PairingCodeResult> {
  const response = await fetchImpl(`${apiBase}/v1/devices/pairing-codes`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
    // Tanpa `deviceNo`, pelayan mengagihkan nombor seterusnya (elak dua kod bertembung).
    body: JSON.stringify({
      outlet_id: input.outletId,
      name: input.name,
      ...(input.deviceNo === undefined ? {} : { device_no: input.deviceNo }),
    }),
  });
  if (!response.ok) throw await parseError(response);
  return (await response.json()) as PairingCodeResult;
}
/**
 * Log keluar.
 *
 * Membatalkan sesi pada PELAYAN (`revoked_at`), bukan sekadar melupakan token pada pelayar. Tanpa
 * ini, sesi yang "keluar" masih sah selama 12 jam jika token itu pernah tersalin.
 *
 * Identiti peranti TIDAK terjejas: kasir terus berjual selepas pemilik log keluar.
 */
export async function logout(
  apiBase: string,
  token: string,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  try {
    await fetchImpl(`${apiBase}/v1/auth/logout`, {
      method: 'POST',
      headers: { authorization: `Bearer ${token}` },
    });
  } catch {
    // Rangkaian gagal: token tempatan tetap dibuang oleh pemanggil. Sesi akan luput sendiri.
  }
}
// ---------------------------------------------------------------- sesi

export interface SessionInfo {
  tenant: { id: string; name: string; slug: string };
  outlets: Array<{ id: string; name: string; code: string }>;
  /** Akaun yang sedang log masuk — diperlukan oleh panel Tetapan. */
  user: { name: string; email: string };
  role: string;
  /** Tetapan operasi kedai yang diperlukan back office. */
  settings: {
    tax_enabled?: boolean;
    receipt_header?: string[];
    receipt_footer?: string;
    trial_started_on?: string | undefined;
  };
}

/**
 * Maklumat sesi pemilik: kedai dan cawangannya.
 *
 * Diambil daripada SESI, bukan daripada identiti peranti. Tablet atau PC yang hanya digunakan untuk
 * melihat laporan tidak mempunyai peranti, jadi identiti peranti tidak boleh menjadi sumber cawangan.
 */
export async function getSessionInfo(
  apiBase: string,
  token: string,
  fetchImpl: typeof fetch = fetch,
): Promise<SessionInfo> {
  const response = await fetchImpl(`${apiBase}/v1/auth/me`, {
    headers: { authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw await parseError(response);

  const body = (await response.json()) as {
    tenant?: { id?: string; name?: string; slug?: string; settings?: Record<string, unknown> };
    outlets?: Array<{ id: string; name: string; code: string }>;
    user?: { name?: string; email?: string };
    role?: string;
  };

  return {
    tenant: {
      id: body.tenant?.id ?? '',
      name: body.tenant?.name ?? '',
      slug: body.tenant?.slug ?? '',
    },
    outlets: body.outlets ?? [],
    user: { name: body.user?.name ?? '', email: body.user?.email ?? '' },
    role: body.role ?? '',
    settings: {
      tax_enabled: body.tenant?.settings?.tax_enabled === true,
      receipt_header: Array.isArray(body.tenant?.settings?.receipt_header)
        ? (body.tenant?.settings?.receipt_header as string[])
        : [],
      receipt_footer:
        typeof body.tenant?.settings?.receipt_footer === 'string'
          ? (body.tenant?.settings?.receipt_footer as string)
          : '',
      trial_started_on:
        typeof body.tenant?.settings?.trial_started_on === 'string'
          ? (body.tenant?.settings?.trial_started_on as string)
          : undefined,
    },
  };
}
export interface RevokeResult {
  device_id: string;
  status: string;
  device_no: number;
}

/**
 * Cabut peranti.
 *
 * Kunci peribadi peranti terletak pada PC itu; jika PC hilang atau dijual, kunci itu masih boleh
 * menandatangani jualan. Mencabut menutup akses tanpa memadam sejarah (resit lama kekal).
 */
export async function revokeDevice(
  apiBase: string,
  token: string,
  deviceId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<RevokeResult> {
  const response = await fetchImpl(`${apiBase}/v1/devices/${deviceId}/revoke`, {
    method: 'POST',
    headers: { authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw await parseError(response);
  return (await response.json()) as RevokeResult;
}
/**
 * Luluskan peranti.
 *
 * Semakan manusia yang menggantikan kata laluan pemilik: pengurus melihat nama, IP dan masa, kemudian
 * membenarkan peranti itu menerima wang.
 */
export async function approveDevice(
  apiBase: string,
  token: string,
  deviceId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<{ device_id: string; status: string; device_no: number }> {
  const response = await fetchImpl(`${apiBase}/v1/devices/${deviceId}/approve`, {
    method: 'POST',
    headers: { authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw await parseError(response);
  return (await response.json()) as { device_id: string; status: string; device_no: number };
}
/**
 * Tetapan akaun dan perniagaan.
 *
 * Semua fungsi di sini mengubah sesuatu yang penting, jadi setiap satu memerlukan sesi pemilik dan
 * (untuk e-mel serta kata laluan) kata laluan semasa.
 */
export async function updateProfile(
  apiBase: string,
  token: string,
  name: string,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  const response = await fetchImpl(`${apiBase}/v1/auth/me`, {
    method: 'PATCH',
    headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  if (!response.ok) throw await parseError(response);
}

export async function changePassword(
  apiBase: string,
  token: string,
  currentPassword: string,
  newPassword: string,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  const response = await fetchImpl(`${apiBase}/v1/auth/password`, {
    method: 'POST',
    headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
  if (!response.ok) throw await parseError(response);
}

export async function changeEmail(
  apiBase: string,
  token: string,
  currentPassword: string,
  email: string,
  fetchImpl: typeof fetch = fetch,
): Promise<{ email: string }> {
  const response = await fetchImpl(`${apiBase}/v1/auth/email`, {
    method: 'POST',
    headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
    body: JSON.stringify({ current_password: currentPassword, email }),
  });
  if (!response.ok) throw await parseError(response);
  return (await response.json()) as { email: string };
}

export async function renameTenant(
  apiBase: string,
  token: string,
  name: string,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  const response = await fetchImpl(`${apiBase}/v1/auth/tenant`, {
    method: 'PATCH',
    headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  if (!response.ok) throw await parseError(response);
}
